import type { CooperationOutcome, LexData } from "./types";

export interface CooperationReputation {
  firmId: string;
  firmName: string;
  jurisdiction: string;
  requestsReceived: number;
  requestsAccepted: number;
  completedCooperations: number;
  introductionsMade: number;
  debriefsSubmitted: number;
  acceptanceRate: number | null;
  averageRating: number | null;
  responsivenessLabel: string;
  trustSignals: string[];
}

function ratingFrom(outcome: CooperationOutcome) {
  const ratings = [outcome.requesterRating, outcome.recipientRating].filter((value): value is number => typeof value === "number" && value > 0);
  if (ratings.length === 0) return null;
  return ratings.reduce((sum, value) => sum + value, 0) / ratings.length;
}

/**
 * Trusted Cooperation reputation. This recognises reliable collaborators, but it
 * is deliberately walled off from governance: cooperation NEVER affects votes or
 * voting weight. No client names, matter values or fees are involved — only
 * whether members respond, accept and follow through.
 */
export function buildCooperationReputation(data: LexData, firmId: string): CooperationReputation | null {
  const firm = data.firms.find((item) => item.id === firmId);
  if (!firm) return null;

  const received = data.cooperationRequests.filter((request) => request.targetFirmId === firmId);
  const accepted = received.filter((request) => ["accepted", "introduced", "in_progress", "completed", "closed"].includes(request.status));
  const requestIds = new Set(data.cooperationRequests.filter((request) => request.targetFirmId === firmId || request.requesterFirmId === firmId).map((request) => request.id));
  const outcomes = data.cooperationOutcomes.filter((outcome) => requestIds.has(outcome.cooperationRequestId));
  const completedCooperations = outcomes.filter((outcome) => outcome.outcomeType === "completed" || outcome.outcomeType === "cooperation_started").length;
  const introductionsMade = outcomes.filter((outcome) => outcome.outcomeType === "introduction_made").length;
  const debriefsSubmitted = data.cooperationEvents.filter((event) => (event.initiatingFirmId === firmId || event.receivingFirmId === firmId) && event.knowledgeDebriefSubmitted).length;

  const ratings = outcomes.map(ratingFrom).filter((value): value is number => value !== null);
  const averageRating = ratings.length > 0 ? ratings.reduce((sum, value) => sum + value, 0) / ratings.length : null;
  const acceptanceRate = received.length > 0 ? accepted.length / received.length : null;

  const responsivenessLabel =
    acceptanceRate === null
      ? "No requests yet"
      : acceptanceRate >= 0.75
        ? "Highly responsive"
        : acceptanceRate >= 0.4
          ? "Responsive"
          : "Selective";

  const trustSignals: string[] = [];
  if (completedCooperations >= 3) trustSignals.push("Repeat trusted collaborator");
  if (debriefsSubmitted >= 1) trustSignals.push("Shares knowledge debriefs");
  if (averageRating !== null && averageRating >= 4) trustSignals.push("Highly rated cooperation");
  if (firm.acceptsCooperationRequests) trustSignals.push("Open to cooperation");

  return {
    firmId,
    firmName: firm.name,
    jurisdiction: firm.jurisdiction,
    requestsReceived: received.length,
    requestsAccepted: accepted.length,
    completedCooperations,
    introductionsMade,
    debriefsSubmitted,
    acceptanceRate,
    averageRating,
    responsivenessLabel,
    trustSignals,
  };
}
